/**
 * Method Journey — the five-phase arc a Be Bold member walks.
 *
 *   Year 1:  Foundation  →  Basic  →  Intermediate
 *   Year 2:  Advanced    →  Mastery
 *
 * This file is the *narrative* view of the program — what the woman
 * sees when she taps "Your journey" on the home screen. It is not
 * tied to the per-week data in year1.js / year2.js; that data keeps
 * driving the session engine. Here we distil ~104 weeks into five
 * chapters she can hold in her head.
 *
 * Each phase carries:
 *   - key             — stable identifier, used for routing / keys
 *   - year            — 1 | 2
 *   - title           — short name ("Foundation")
 *   - eyebrow         — a positioning line above the title
 *                       ("Weeks 1–12 · Meet your floor")
 *   - weekRange       — [startWeek, endWeek] within its year, so
 *                       current-position highlighting is derived
 *   - summary         — one or two sentences describing the chapter
 *   - skills          — a short list of named movements the woman
 *                       *will learn* during this phase. Each skill is
 *                       a locale key whose value is the display name.
 *   - verbKey         — a representative named-skill verb for the
 *                       phase's CoachMark illustration
 *   - accent          — a small palette tweak for this phase's card
 *                       ('rose' / 'sage' / 'gold' / 'mauve' / 'deep')
 *   - milestoneAt     — the streak-day or week milestone the phase
 *                       completion corresponds to, for the locked /
 *                       unlocked state.
 *
 * All text is delivered via locale keys so EN + PT both carry the
 * same shape.
 */

export const JOURNEY_PHASES = [
  {
    key: 'foundation',
    year: 1,
    weekRange: [1, 16],
    titleKey:   'journey_foundation_title',
    eyebrowKey: 'journey_foundation_eyebrow',
    summaryKey: 'journey_foundation_summary',
    skillKeys:  ['rp_state_squeeze', 'rp_state_release', 'rp_state_push'],
    verbKey:    'rp_state_squeeze',
    accent:     'rose',
  },
  {
    key: 'basic',
    year: 1,
    weekRange: [17, 32],
    titleKey:   'journey_basic_title',
    eyebrowKey: 'journey_basic_eyebrow',
    summaryKey: 'journey_basic_summary',
    skillKeys:  ['rp_state_squeeze', 'rp_state_pulse', 'rp_state_lift'],
    verbKey:    'rp_state_pulse',
    accent:     'sage',
  },
  {
    key: 'intermediate',
    year: 1,
    weekRange: [33, 52],
    titleKey:   'journey_intermediate_title',
    eyebrowKey: 'journey_intermediate_eyebrow',
    summaryKey: 'journey_intermediate_summary',
    // Last chapter of Year 1 — the first time walls (front, back,
    // lateral) are introduced. Foundation + Basic stayed central;
    // Intermediate teaches directional engagement and hands it off
    // to Year 2.
    skillKeys:  ['rp_state_tilt', 'rp_state_grip', 'rp_state_pull'],
    verbKey:    'rp_state_tilt',
    accent:     'mauve',
  },
  {
    key: 'advanced',
    year: 2,
    weekRange: [1, 26],
    titleKey:   'journey_advanced_title',
    eyebrowKey: 'journey_advanced_eyebrow',
    summaryKey: 'journey_advanced_summary',
    skillKeys:  ['rp_state_lock', 'rp_state_tilt', 'rp_state_twist'],
    verbKey:    'rp_state_lock',
    accent:     'gold',
  },
  {
    key: 'mastery',
    year: 2,
    weekRange: [27, 52],
    titleKey:   'journey_mastery_title',
    eyebrowKey: 'journey_mastery_eyebrow',
    summaryKey: 'journey_mastery_summary',
    skillKeys:  ['rp_state_lock', 'rp_state_twist', 'rp_state_draw', 'rp_state_lift'],
    verbKey:    'rp_state_twist',
    accent:     'deep',
  },
];

/**
 * Given a userProgress snapshot, derive which phase the user is
 * currently inside, which are completed, and which are still locked.
 * Returns an array of { phase, state } where state is one of:
 *   'done'     — fully behind the user
 *   'current'  — the user is actively in this phase this week
 *   'next'     — the phase immediately after the current one
 *   'locked'   — not yet earned
 *
 * When userProgress is null/undefined (new user, pre-assessment),
 * all phases are 'locked' except the first which is 'current'.
 */
export function resolveJourneyState(userProgress) {
  // Clamp to the valid program range so out-of-range or missing
  // data still produces a sensible journey view instead of
  // silently marking every phase the wrong state.
  const rawYear = Number.isFinite(userProgress?.current_year) ? userProgress.current_year : 1;
  const rawWeek = Number.isFinite(userProgress?.current_week) ? userProgress.current_week : 1;
  const year = Math.min(Math.max(1, rawYear), 2);
  const week = Math.max(1, rawWeek);
  let seenCurrent = false;
  let nextAssigned = false;
  return JOURNEY_PHASES.map((phase) => {
    const [start, end] = phase.weekRange;
    let state;
    if (phase.year < year) {
      state = 'done';
    } else if (phase.year === year) {
      if (week > end) {
        state = 'done';
      } else if (week >= start) {
        state = 'current';
        seenCurrent = true;
      } else if (seenCurrent && !nextAssigned) {
        state = 'next';
        nextAssigned = true;
      } else {
        state = 'locked';
      }
    } else if (seenCurrent && !nextAssigned) {
      state = 'next';
      nextAssigned = true;
    } else {
      state = 'locked';
    }
    return { phase, state };
  });
}
